import axios from "utils/axiosConfig";
import { useState } from "react";
import { urlService } from "infra/envs/";
import useNotifications from "hooks/useNotifications";
import { useDispatch, useSelector } from "react-redux";
import {
    initialLoadStarted,
    pageChangeStarted,
    processesFailed,
    processesReceived,
    processAdded,
    pageChanged,
    mutationStarted,
    mutationCompleted,
    processesRequested,
} from "reducers/candidate/recruitmentProcessesSlice";
import { updateCandidateVersion } from "reducers/candidate/currentCandidateSlice";

export const extractErrorMessage = (errorObject = {}) => {
    const keys = Object.keys(errorObject || {});
    if (keys.length === 0) return null;
    const firstKey = keys[0];
    const firstErrorMessage = errorObject[firstKey];
    return Array.isArray(firstErrorMessage)
        ? firstErrorMessage.join(" ")
        : `${firstErrorMessage}`;
};

const ACTIONS = {
    ADD_RECRUITMENT_PROCESS: "addRecruitmentProcesses",
    FETCH_RECRUITMENT_PROCESSES: "fetchRecruitmentProcesses",
    UPDATE_RECRUITMENT_PROCESS_STATE: "updateRecruitmentProcessState",
    CREATE_NOTE: "createNote",
    EDIT_NOTE: "editNote",
    DELETE_NOTE: "deleteNote",
    ADD_STEP: "addStep",
    ATTACH_JOB: "attachJob",
    SUBMIT_HIRED_FORM_DATA: "submitHiredFormData",
};

const generateEndpoint = (action, params) => {
    const {
        candidateId,
        recruitmentProccessId,
        flowId,
        rpStepStateId,
        noteId,
    } = params;

    const getCandidateId = params;

    switch (action) {
        case ACTIONS.ADD_RECRUITMENT_PROCESS:
            return `${urlService.CANDIDATES_API_PATH}/${getCandidateId}/recruitment-processes`;

        case ACTIONS.FETCH_RECRUITMENT_PROCESSES:
            return `${urlService.CANDIDATES_API_PATH}/${getCandidateId}/recruitment-processes`;

        case ACTIONS.UPDATE_RECRUITMENT_PROCESS_STATE:
            return `${urlService.CANDIDATES_API_PATH}/${candidateId}/recruitment-processes/${recruitmentProccessId}/flows/${flowId}/steps/${rpStepStateId}/statuses`;

        case ACTIONS.CREATE_NOTE:
            return `${urlService.CANDIDATES_API_PATH}/${candidateId}/recruitment-processes/${recruitmentProccessId}/flows/${flowId}/steps/${rpStepStateId}/notes`;

        case ACTIONS.EDIT_NOTE:
            return `${urlService.CANDIDATES_API_PATH}/${candidateId}/recruitment-processes/${recruitmentProccessId}/flows/${flowId}/steps/${rpStepStateId}/notes/${noteId}`;

        case ACTIONS.DELETE_NOTE:
            return `${urlService.CANDIDATES_API_PATH}/${candidateId}/recruitment-processes/${recruitmentProccessId}/flows/${flowId}/steps/${rpStepStateId}/notes/${noteId}`;

        case ACTIONS.ADD_STEP:
            return `${urlService.CANDIDATES_API_PATH}/${candidateId}/recruitment-processes/${recruitmentProccessId}/flows/${flowId}/steps`;

        case ACTIONS.ATTACH_JOB:
            return `${urlService.CANDIDATES_API_PATH}/${candidateId}/recruitment-processes/${recruitmentProccessId}/jobs`;

        case ACTIONS.SUBMIT_HIRED_FORM_DATA:
            return `${urlService.CANDIDATES_API_PATH}/${candidateId}/recruitment-processes/${recruitmentProccessId}/flows/${flowId}/hired_infos`;

        default:
            throw new Error(`Unsupported action: ${action}`);
    }
};

export const useRecruitmentProcess = () => {
    const [isLoading, setIsLoading] = useState(false);
    const [requestSuccessful, setRequestSuccessful] = useState(false);
    const [updateNoteRequestSuccessful, setUpdateNoteRequestSuccessful] =
        useState(false);
    const [deleteNoteRequestSuccessful, setDeleteNoteRequestSuccessful] =
        useState(false);
    const { renderNotification, renderErrorNotification } = useNotifications();
    const dispatch = useDispatch();
    const currentPage = useSelector(
        (state) => state.recruitmentProcesses.currentPage,
    );
    const candidateId = useSelector((state) => state.currentCandidate.id);

    const getRecruitmentProcesses = async (
        candidateId,
        page = 1,
        isInitial = false,
    ) => {
        setIsLoading(true);
        dispatch(processesRequested());
        if (isInitial) dispatch(initialLoadStarted());
        else dispatch(pageChangeStarted());

        const baseEndpoint = generateEndpoint(
            ACTIONS.FETCH_RECRUITMENT_PROCESSES,
            candidateId,
        );
        const endpoint = `${baseEndpoint}?page=${page}`;

        try {
            const response = await axios.get(endpoint);
            const candidateRecruitmentProcesses = response.data;
            dispatch(processesReceived(candidateRecruitmentProcesses));
            setRequestSuccessful(true);
            return candidateRecruitmentProcesses;
        } catch (error) {
            dispatch(processesFailed(error?.message || "Request failed"));
            const errorMessage = extractErrorMessage(error?.response?.data);
            setRequestSuccessful(false);
            renderErrorNotification(errorMessage || "Request failed");
            throw new Error("Failed to fetch recruitment processes");
        } finally {
            setIsLoading(false);
        }
    };

    const changePage = async (candidateId, page) => {
        await getRecruitmentProcesses(candidateId, page, false);
        dispatch(pageChanged(page));
    };

    const updateRecruitmentProcessState = async (
        endpointParams,
        updateData,
    ) => {
        dispatch(mutationStarted());
        const endpoint = generateEndpoint(
            ACTIONS.UPDATE_RECRUITMENT_PROCESS_STATE,
            endpointParams,
        );
        try {
            await axios.post(endpoint, updateData);
            await getRecruitmentProcesses(candidateId, currentPage);
            setRequestSuccessful(true);
            renderNotification(`Status succesfully changed`);
        } catch (error) {
            const errorMessage = extractErrorMessage(error?.response?.data);
            setRequestSuccessful(false);
            renderErrorNotification(errorMessage || "Failed to update status");
            throw new Error("Failed to update status");
        } finally {
            dispatch(mutationCompleted());
        }
    };

    const createRecruitmentProcessNote = async (endpointParams, updateData) => {
        dispatch(mutationStarted());
        const endpoint = generateEndpoint(ACTIONS.CREATE_NOTE, endpointParams);
        try {
            await axios.post(endpoint, updateData);
            await getRecruitmentProcesses(candidateId, currentPage);
            setRequestSuccessful(true);
            renderNotification(`Note succesfully created`);
        } catch (error) {
            const errorMessage = extractErrorMessage(error?.response?.data);
            setRequestSuccessful(false);
            renderErrorNotification(errorMessage || "Failed to create note");
            throw new Error("Failed to create note");
        } finally {
            dispatch(mutationCompleted());
        }
    };

    const updateRecruitmentProcessNote = async (endpointParams, updateData) => {
        dispatch(mutationStarted());
        const endpoint = generateEndpoint(ACTIONS.EDIT_NOTE, endpointParams);
        try {
            await axios.patch(endpoint, updateData);
            await getRecruitmentProcesses(candidateId, currentPage);
            setUpdateNoteRequestSuccessful(true);
            renderNotification(`Succesfully updated note`);
        } catch (error) {
            const errorMessage = extractErrorMessage(error?.response?.data);
            setUpdateNoteRequestSuccessful(false);
            renderErrorNotification(errorMessage || "Failed to update note");
            throw new Error("Failed to update note");
        } finally {
            dispatch(mutationCompleted());
        }
    };

    const deleteRecruitmentProcessNote = async (endpointParams) => {
        dispatch(mutationStarted());
        const endpoint = generateEndpoint(ACTIONS.DELETE_NOTE, endpointParams);
        try {
            await axios.delete(endpoint);
            await getRecruitmentProcesses(candidateId, currentPage);
            setDeleteNoteRequestSuccessful(true);
            renderNotification(`Succesfully deleted note`);
        } catch (error) {
            const errorMessage = extractErrorMessage(error?.response?.data);
            setDeleteNoteRequestSuccessful(false);
            renderErrorNotification(errorMessage || "Failed to delete note");
            throw new Error("Failed to delete note");
        } finally {
            dispatch(mutationCompleted());
        }
    };

    return {
        getRecruitmentProcesses,
        changePage,
        updateRecruitmentProcessState,
        createRecruitmentProcessNote,
        updateRecruitmentProcessNote,
        deleteRecruitmentProcessNote,
        isLoading,
        requestSuccessful,
        updateNoteRequestSuccessful,
        deleteNoteRequestSuccessful,
    };
};

export const useAddRecuitmentProcess = () => {
    const [isLoading, setIsLoading] = useState(false);
    const [requestSuccessful, setRequestSuccessful] = useState(false);
    const { renderNotification, renderErrorNotification } = useNotifications();
    const dispatch = useDispatch();

    const addRecuitmentProcess = async (candidateId, data) => {
        setIsLoading(true);
        dispatch(mutationStarted());
        try {
            const response = await axios.post(
                generateEndpoint(ACTIONS.ADD_RECRUITMENT_PROCESS, candidateId),
                data,
            );
            const candidateRecruitmentProcesses = response.data;
            dispatch(processAdded(candidateRecruitmentProcesses));
            dispatch(mutationCompleted());
            dispatch(updateCandidateVersion());
            setRequestSuccessful(true);
            renderNotification("Recruitment process created successfully");
            return { status: response.status };
        } catch (error) {
            const errorMessage = extractErrorMessage(error?.response?.data);
            setRequestSuccessful(false);
            renderErrorNotification(
                errorMessage || "Failed to create recruitment process",
            );
            throw new Error("Failed to create recruitment process");
        } finally {
            setIsLoading(false);
        }
    };

    return { addRecuitmentProcess, isLoading, requestSuccessful };
};

export const useAddStep = () => {
    const [requestSuccessful, setRequestSuccessful] = useState(false);
    const { renderNotification, renderErrorNotification } = useNotifications();
    const dispatch = useDispatch();
    const currentPage = useSelector(
        (state) => state.recruitmentProcesses.currentPage,
    );
    const candidateId = useSelector((state) => state.currentCandidate.id);
    const { getRecruitmentProcesses } = useRecruitmentProcess();

    const addStep = async (endpointParams, data) => {
        dispatch(mutationStarted());
        try {
            await axios.post(
                generateEndpoint(ACTIONS.ADD_STEP, endpointParams),
                data,
                { headers: { "Content-Type": "application/json" } },
            );
            await getRecruitmentProcesses(candidateId, currentPage);
            setRequestSuccessful(true);
            renderNotification("Step created successfully");
            return { status: 200 };
        } catch (error) {
            const errorMessage = extractErrorMessage(error?.response?.data);
            setRequestSuccessful(false);
            renderErrorNotification(errorMessage || "Failed to create step");
            throw new Error("Failed to create step");
        } finally {
            dispatch(mutationCompleted());
        }
    };

    return { addStep, requestSuccessful };
};

export const useAttachJobs = () => {
    const [requestSuccessful, setRequestSuccessful] = useState(false);
    const { renderNotification, renderErrorNotification } = useNotifications();
    const dispatch = useDispatch();
    const currentPage = useSelector(
        (state) => state.recruitmentProcesses.currentPage,
    );
    const candidateId = useSelector((state) => state.currentCandidate.id);
    const { getRecruitmentProcesses } = useRecruitmentProcess();

    const attachJobs = async (endpointParams, data) => {
        dispatch(mutationStarted());
        try {
            await axios.post(
                generateEndpoint(ACTIONS.ATTACH_JOB, endpointParams),
                data,
                { headers: { "Content-Type": "application/json" } },
            );
            await getRecruitmentProcesses(candidateId, currentPage);
            setRequestSuccessful(true);
            renderNotification("Attached job successfully");
            return { status: 200 };
        } catch (error) {
            const errorMessage = extractErrorMessage(error?.response?.data);
            setRequestSuccessful(false);
            renderErrorNotification(errorMessage || "Failed to attach job");
            throw new Error("Failed to attach job");
        } finally {
            dispatch(mutationCompleted());
        }
    };

    return { attachJobs, requestSuccessful };
};

export const useSubmitHiredFormData = () => {
    const [isLoading, setIsLoading] = useState(false);
    const [isRequestSuccessfull, setIsRequestSuccessfull] = useState(false);
    const { renderNotification, renderErrorNotification } = useNotifications();

    const submitHiredFormData = async (endpointParams, formData) => {
        setIsLoading(true);
        const endpoint = generateEndpoint(
            ACTIONS.SUBMIT_HIRED_FORM_DATA,
            endpointParams,
        );

        try {
            const response = await axios.post(endpoint, formData);
            const {
                data: { message },
            } = response;
            renderNotification(message);
            setIsRequestSuccessfull(true);
        } catch (error) {
            const errorMessage = extractErrorMessage(error?.response?.data);
            renderErrorNotification(errorMessage || "Failed to submit form");
        } finally {
            setIsLoading(false);
            setIsRequestSuccessfull(false);
        }
    };

    return { submitHiredFormData, isRequestSuccessfull, isLoading };
};
